<template>
  <div class="c-domain-setup-form pt-4">

    <div>
      <ListView @open-integration="handleIntegrationOpen"/>
    </div>

    <div class="mt-16 mb-12 flex justify-center text-sm"><strong>Add more <i class="el-icon-bottom"></i></strong></div>

    <div class="w-full p-3 flex items-center gap-2">
      <img src="/integrations/zapier-logo.svg" alt="Zapier Logo"
           width="130" class="mr-4 md:mr-8">
      <div class="w-full flex justify-between">
        <div class="max-w-md flex flex-col gap-y-2">
          <h3 class="font-bold text-xl">Use Zapier to integrate this widget with hundreds of other apps</h3>
          <a href="https://onvocado.com/support/integrating-onvocado-with-zapier" class="underline text-sm" target="_blank">
            Read more about Exporting data to zapier
          </a>
        </div>

        <div>
          <button class="btn secondary outline medium m-auto mb-2"
                  type="button"
                  @click.stop="handleIntegrationOpen('', 'ZAPIER')">Add Zapier Webhook
          </button>
        </div>
      </div>
    </div>

    <div class="w-full p-3 flex items-center gap-2">
      <div class="w-40 flex-shrink-0">
        <img src="/integrations/webhook.svg" alt="Webhook Logo"
             class="m-auto">
      </div>

      <div class="w-full flex justify-between">
        <div class="max-w-md flex flex-col gap-y-2">
          <h3 class="font-bold text-xl">Use Webhooks to automate data transfers between your applications</h3>
          <a href="https://onvocado.com/support/setting-up-onvocado-webhooks" class="underline text-sm" target="_blank">
            Learn more about how Webhooks can streamline your workflows
          </a>
        </div>

        <div>
          <button class="btn secondary outline medium m-auto mb-2"
                  type="button"
                  @click.stop="handleIntegrationOpen('', 'WEBHOOK')">Add Plain Webhook
          </button>
        </div>
      </div>
    </div>

    <div class="w-full p-3 flex items-center gap-2">
      <div class="w-40 flex-shrink-0">
        <img src="/integrations/mailchimp.svg" alt="Mailchimp Logo" class="m-auto"/>
      </div>

      <div class="w-full flex justify-between">
        <div class="max-w-md flex flex-col gap-y-2">
          <h3 class="font-bold text-xl">Connect with Mailchimp to sync your contacts</h3>
          <a href="https://onvocado.com/support/setting-up-mailchimp-integration" class="underline text-sm" target="_blank">
            Learn more about integrating with Mailchimp
          </a>
        </div>

        <div>
          <button
              v-if="!hasMailchimpIntegration"
              class="btn secondary outline medium m-auto mb-2"
              type="button"
              :disabled="hasMailchimpIntegration"
              @click.stop="connectToMailchimp">
            Connect to Mailchimp
          </button>
          <span v-else>
            Active
          </span>
        </div>
      </div>
    </div>

        <!-- ActiveCampaign Integration Section -->
    <div class="w-full p-3 flex items-center gap-2">
      <div class="w-40 flex-shrink-0">
        <img src="/integrations/activecampaign.svg" alt="ActiveCampaign Logo" class="m-auto"/>
      </div>

      <div class="w-full flex justify-between">
        <div class="max-w-md flex flex-col gap-y-2">
          <h3 class="font-bold text-xl">Connect with ActiveCampaign to manage your contacts</h3>
          <a href="https://onvocado.com/support/setting-up-activecampaign-integration" class="underline text-sm" target="_blank">
            Learn more about integrating with ActiveCampaign
          </a>
        </div>

        <div>
          <button
              v-if="!hasActiveCampaignIntegration"
              class="btn secondary outline medium m-auto mb-2"
              type="button"
              @click.stop="handleIntegrationOpen('', 'ACTIVECAMPAIGN')">
            Connect to ActiveCampaign
          </button>
          <span v-else>
            Active
          </span>
        </div>
      </div>
    </div>

    <el-drawer
        :size="'70%'"
        class="c-drawer"
        :visible.sync="drawerVisible"
        :before-close="handleClose"
        :destroy-on-close="true">
      <div slot="title" class="mb-0 text-body-black">
        <strong> {{ targetIntegrationID ? '' : 'New' }} <i class="capitalize">{{ targetIntegrationType }}</i>
          integration</strong>
        <hr class="mt-2">
      </div>

      <Webhook v-if="targetIntegrationType === 'WEBHOOK'"
               @form-modified="childFormModified = $event"
               @close-drawer="triggerDrawerClose"
               @force-close="triggerForceClose"/>

      <Zapier v-if="targetIntegrationType === 'ZAPIER'"
              @form-modified="childFormModified = $event"
              @close-drawer="triggerDrawerClose"
              @force-close="triggerForceClose"/>

      <Mailchimp
          v-if="targetIntegrationType === 'MAILCHIMP'"
          @form-modified="childFormModified = $event"
          @close-drawer="triggerDrawerClose"
          @force-close="triggerForceClose"/>

      <ActiveCampaign
          v-if="targetIntegrationType === 'ACTIVECAMPAIGN'"
          @form-modified="childFormModified = $event"
          @close-drawer="triggerDrawerClose"
          @force-close="triggerForceClose"/>
    </el-drawer>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import ListView from '@components/forms/integrations/ListView.vue'
import Zapier from '@components/forms/integrations/Zapier.vue'
import Webhook from '@components/forms/integrations/Webhook.vue'
import Mailchimp from '@components/forms/integrations/Mailchimp.vue'
import ActiveCampaign from '@components/forms/integrations/ActiveCampaign.vue'

export default {
  name: 'Integrations',
  components: {
    Webhook,
    Zapier,
    Mailchimp,
    ActiveCampaign,
    ListView,
  },
  data() {
    return {
      drawerVisible: false,
      targetIntegrationType: null,
      childFormModified: false,
    }
  },
  computed: {
    ...mapState('integration', ['targetIntegrationID']),
    ...mapGetters('integration', ['hasMailchimpIntegration', 'hasActiveCampaignIntegration']),
    targetWidgetID() {
      return this.$store.state.campaign.widget.targetWidgetID
    },
  },
  mounted() {
    window.addEventListener('message', this.receiveMessage, false)
  },
  beforeDestroy() {
    window.removeEventListener('message', this.receiveMessage, false)
  },
  methods: {
    ...mapActions('integration', ['setTargetIntegration', 'listIntegrations']),
    handleIntegrationOpen(id = '', targetIntegration) {
      this.setTargetIntegration(id)
      this.targetIntegrationType = targetIntegration
      this.drawerVisible = true
    },
    handleClose(done) {
      if (this.childFormModified) {
        this.$msgbox
            .confirm('You have unsaved changes. Are you sure you want to exit?')
            .then(() => {
              done()
              this.childFormModified = false
            })
            .catch(() => {})
      } else {
        done()
      }
    },
    triggerDrawerClose() {
      this.handleClose(() => {
        this.drawerVisible = false
        this.targetIntegrationType = null
      })
    },
    triggerForceClose() {
      this.drawerVisible = false
      this.childFormModified = false
    },
    connectToMailchimp() {
      // Call the action to initiate OAuth flow
      this.$store.dispatch('integration/initiateMailchimpOAuth', this.targetWidgetID)
    },
    receiveMessage(event) {
      // For security, verify the origin of the message
      const expectedOrigin = window.location.origin

      if (event.origin !== expectedOrigin) {
        console.warn('Received message from unauthorized origin:', event.origin)
        return
      }

      const data = event.data
      if (data.type === 'mailchimp_integration' && data.integrationId) {
        // Reload the integrations list
        this.listIntegrations().then(() => {
          this.setTargetIntegration(data.integrationId)
          this.targetIntegrationType = 'MAILCHIMP'
          this.drawerVisible = true
        }).catch(error => {
          console.error('Failed to list integrations:', error)
        })
      }
    },
  },
}
</script>

<style lang="scss">
.c-drawer {
  .el-drawer__header {
    @apply mb-0;
  }
  .el-drawer__body {
    @apply pt-6 overflow-y-auto;
  }
}
</style>